import React, { useState } from 'react';
import { generateReleaseGuidance } from '../services/geminiService';
import { ReleaseGuidance } from '../types';
import { Flame, RefreshCw, Feather, ArrowRight, Trash2 } from 'lucide-react';
import { useUser } from '../context/UserContext';

const LettingGoCeremony: React.FC = () => {
  const [burden, setBurden] = useState('');
  const [guidance, setGuidance] = useState<ReleaseGuidance | null>(null);
  const [loading, setLoading] = useState(false);
  const [isBurned, setIsBurned] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleConsult = async () => {
    if (!burden.trim()) return;
    setLoading(true);
    setGuidance(null);
    setIsBurned(false);
    try {
      const data = await generateReleaseGuidance(burden);
      setGuidance(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const burnIt = () => {
    setIsBurned(true);
    addXp(50, 'Completed Release Ceremony');
    unlockBadge('phoenix');
    // After animation, reset
    setTimeout(() => {
        setBurden('');
        setGuidance(null);
        setIsBurned(false);
    }, 4000);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-red-50 text-red-600 rounded-full mb-4">
          <Flame size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Release Ceremony</h1>
        <p className="text-nunchi-600">
          Identify what weighs you down. Gain a new perspective. <br/>Then, burn it away.
        </p>
      </header>

      {!guidance && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What are you holding onto?
            </label>
            <textarea
              value={burden}
              onChange={(e) => setBurden(e.target.value)}
              placeholder="e.g., Anger towards my ex, Guilt about a missed opportunity, Fear of failure..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-red-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handleConsult}
              disabled={!burden.trim()}
              className="w-full bg-indigo-700 text-white py-4 rounded-xl font-bold hover:bg-indigo-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Feather size={18} /> Seek Perspective
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-indigo-700">Untangling the knots...</p>
        </div>
      )}

      {guidance && !isBurned && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 relative overflow-hidden shadow-lg">
                 <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-300 to-red-300"></div>
                 
                 <div className="mb-6">
                     <h3 className="text-indigo-400 uppercase tracking-widest text-xs font-bold mb-2">Perspective Shift</h3>
                     <p className="text-lg leading-relaxed font-serif text-nunchi-800">"{guidance.perspective}"</p>
                 </div>

                 <div className="mb-6 bg-indigo-50 p-4 rounded-xl">
                     <h3 className="text-indigo-600 uppercase tracking-widest text-xs font-bold mb-2">Release Ritual</h3>
                     <p className="text-nunchi-700">{guidance.ritual}</p>
                 </div>

                 <div className="text-center">
                     <h3 className="text-nunchi-400 uppercase tracking-widest text-xs font-bold mb-2">Say this aloud</h3>
                     <p className="text-xl font-bold text-nunchi-900 italic mb-8">"{guidance.affirmation}"</p>
                     
                     <button 
                        onClick={burnIt}
                        className="w-full bg-red-600 text-white py-4 rounded-xl font-bold hover:bg-red-700 transition-all flex justify-center items-center gap-2 shadow-lg hover:shadow-red-200/50"
                     >
                        <Trash2 size={20} /> Burn This Burden
                     </button>
                 </div>
            </div>
        </div>
      )}

      {isBurned && (
          <div className="flex flex-col items-center justify-center py-20 animate-fadeOut text-center">
              <div className="relative mb-4">
                  <Flame size={64} className="text-orange-500 animate-pulse" />
                  <div className="absolute inset-0 bg-orange-400 blur-xl opacity-50 animate-pulse"></div>
              </div>
              <p className="text-xl font-serif font-bold text-nunchi-800">It is gone.</p>
              <p className="text-nunchi-500">You are lighter now.</p>
          </div>
      )}
    </div>
  );
};

export default LettingGoCeremony;